import {Component, computed, inject, signal} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {BaseDesignBlockFile, DesignBlockFilesDialogData} from "./types/design-block-files.types";
import {DesignBlockData} from "../base-design-block-modal/types/base-design-block.types";
import {CarouselComponent} from "../../../../../../shared/components/ui/carousel/carousel.component";
import {
  FileUploadCardComponent
} from "../../../../../../shared/components/ui/file-upload-card/file-upload-card.component";
import {defaultDesignBlockData} from "../base-design-block-modal/configs/default-design-block-data";
import {AttachFileParams, EntityFileType} from "../../../../../../core/types/file.types";
import {designBlockEntityFileTypes, designBlockGetFilesParamKeys} from "./configs/design-block-files-config";
import {DbFilePathStateService} from "./services/db-file-path-state.service";
import {AttachProductCardFileParams, GetFilesParams} from "../../../types/product-card-files.types";
import {DesignBlockFilesService} from "./services/design-block-files.service";
import {FormsModule} from "@angular/forms";
import {LoadingService} from "../../../../../../core/services/loading.service";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {DbFilesStateService} from "./services/db-files-state.service";
import {DbShowFileLibraryStateService} from "./services/db-show-file-library-state.service";
import {DbHeaderModalComponent} from "./components/db-header-modal/db-header-modal.component";
import {DbFileCardComponent} from "./components/db-file-card/db-file-card.component";
import {DbFileLibraryComponent} from "./components/db-file-library/db-file-library.component";
import {DbBottomModalComponent} from "./components/db-bottom-modal/db-bottom-modal.component";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-design-block-files-modal',
  imports: [
    BaseModalComponent,
    CarouselComponent,
    FileUploadCardComponent,
    FormsModule,
    DbHeaderModalComponent,
    DbFileCardComponent,
    DbFileLibraryComponent,
    DbBottomModalComponent,
    LoaderComponent
  ],
  templateUrl: './design-block-files-modal.component.html',
  styleUrl: './design-block-files-modal.component.scss',
  providers: [ LoadingService, EditModeService ]
})
export class DesignBlockFilesModalComponent extends BaseModalComponent {
  public designBlockFilesDialogData = inject<DesignBlockFilesDialogData>(MAT_DIALOG_DATA);
  public dbFilesStateService: DbFilesStateService = inject(DbFilesStateService);
  private dbFilePathStateService: DbFilePathStateService = inject(DbFilePathStateService);
  private designBlockFilesService: DesignBlockFilesService = inject(DesignBlockFilesService);
  private dbShowFileLibraryStateService: DbShowFileLibraryStateService = inject(DbShowFileLibraryStateService);

  public isShowFileLibrary = computed(() => this.dbShowFileLibraryStateService.isShowFileLibrary());
  public modalKey = computed(() => this.designBlockFilesDialogData.modalKey);
  public designBlockData = signal<DesignBlockData>(defaultDesignBlockData);
  public designBlockEntityFileType = computed<EntityFileType>(() => designBlockEntityFileTypes[this.modalKey()] || 'LISTING');
  public isActiveUploadFile: boolean = false;
  public currentIndex: number = 0;

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initComponent();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.modalKey());
    this.dbFilesStateService.resetDbFilesState();
    this.dbShowFileLibraryStateService.resetShowFileLibrary();
  }

  public onIndexChange(index: number): void {
    this.currentIndex = index;
  }

  public uploadFiles(isActiveUploadFile: boolean): void {
    this.isActiveUploadFile = isActiveUploadFile;
  }

  public async attachFile({ parentId, fileId }: AttachFileParams): Promise<void> {
    const attachFileParams: AttachProductCardFileParams = {
      path: this.dbFilePathStateService.pathFileState(),
      body: {
        fileId: fileId,
        [designBlockGetFilesParamKeys[this.modalKey()]]: parentId
      }
    };

    await this.designBlockFilesService.attachFile<BaseDesignBlockFile>(attachFileParams)
      .then((data) => this.dbFilesStateService.updateDbFilesState(data));
  }

  public async updateComment(data: DesignBlockData): Promise<void> {
    this.designBlockData.set(data);
  }

  public updateField(file: BaseDesignBlockFile): void {
    this.dbFilesStateService.updateDbFilesState(file);
  }

  private async initComponent(): Promise<void> {
    this.modalStatesService.openModal(this.modalKey());
    this.designBlockData.set(this.designBlockFilesDialogData[this.modalKey()] as DesignBlockData);
    this.dbFilePathStateService.setPathFileState(this.modalKey());
    await this.loadFiles();
  }

  private async loadFiles(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    const params: GetFilesParams = {
      path: this.dbFilePathStateService.pathFileState(),
      params: {
        [designBlockGetFilesParamKeys[this.modalKey()]]: this.designBlockData().id
      }
    };

    await this.designBlockFilesService.getFiles(params)
      .then((data) => this.dbFilesStateService.setDbFilesState(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }
}
